/**
 * The parent app's design tokens: direction A, "Keçe & Kâğıt" (felt and paper).
 *
 * Chosen by the owner from three mocks on 2026-10-03. The approved board, with
 * every component specimen, is `.docs/design/parent-v2/a-kece-kagit.html#kurallar`.
 *
 * Single source: `theme.ts` publishes each entry as a CSS custom property —
 * `color.paper2` becomes `--kh-color-paper-2` — and the stylesheets only ever
 * read those. A literal colour or size in a stylesheet is a defect.
 *
 * **Every felt colour means one thing.** Amber is time and work in progress.
 * Sage is "the child can see it" and done. Clay is destructive or a problem.
 * Using one for decoration teaches the parent the wrong lesson about it.
 */
export const tokens = {
  color: {
    // Ground
    /** App background. */
    paper: '#F4EEE2',
    /** Wells: segmented track, stepper value, timeline. */
    paper2: '#EBE3D3',
    /** Cards, inputs, sheets. */
    sheet: '#FFFCF6',
    /** Card edges and row dividers; decorative only. */
    line: '#E1D7C5',
    /** Control borders: 3:1 against `sheet`. */
    lineStrong: '#8F826B',

    // Ink
    /** Text, primary buttons, the sidebar, the "now" card. */
    ink: '#1E2542',
    ink2: '#2B3356',
    text2: '#575047',
    /** Tertiary text, still AA on every ground. */
    text3: '#6B6458',
    onInk: '#F4EFE5',
    onInk2: '#B8B7C6',

    // Felt accents
    amber: '#C8871F',
    amberHigh: '#E3A84A',
    amberText: '#8A5A11',
    amberWash: '#F6E7C8',
    sage: '#4C7758',
    sageHigh: '#9CC3A4',
    sageText: '#3D6548',
    sageWash: '#E1EADB',
    clay: '#A94A31',
    clayHigh: '#F0A48E',
    clayText: '#9A4129',
    clayWash: '#F5E0D7',
    /** Pressed destructive button. */
    clayDeep: '#923E28',

    // States
    /** A switch that is off. */
    switchOff: '#9C907B',
    /** A control that cannot be used. */
    textDisabled: '#A89D88',
    /** The brightest text on ink: the big numbers in the "now" card. */
    onInkStrong: '#FFF8EA',
    /** Text, ticks and knobs on a filled felt surface: a switch knob, a checked box. */
    onFelt: '#FFFFFF',
    /** The camera viewfinder behind a QR scan. */
    viewfinder: '#151A2E',

    // Poster felts, for content without a picture of its own
    feltNiloya: '#7FA487',
    feltPepee: '#D9A441',
    feltSong: '#5D6A8C',
    feltGame: '#8C6F93',
    feltSleep: '#4E5A78',
  },
  font: {
    /** Headings and large numbers. Alegreya: humanist, full Turkish coverage. */
    serif:
      '"Alegreya Variable", "Alegreya", "Iowan Old Style", "Palatino Linotype", Palatino, Georgia, serif',
    /** Everything else: the platform's own face. */
    sans: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  /** A four-pixel rhythm. */
  space: {
    s1: '4px',
    s2: '8px',
    s3: '12px',
    s4: '16px',
    s5: '20px',
    s6: '24px',
    s7: '32px',
    s8: '40px',
    s9: '56px',
  },
  radius: {
    r1: '8px',
    r2: '12px',
    r3: '18px',
    r4: '24px',
    pill: '999px',
  },
  shadow: {
    /** Resting cards. */
    e1: '0 1px 0 rgba(30,37,66,.04), 0 1px 3px rgba(30,37,66,.07)',
    /** The bottom tab bar, lifted off the page. */
    e2: '0 -1px 0 rgba(30,37,66,.06), 0 -6px 20px rgba(30,37,66,.08)',
    /** Sheets and dialogs. */
    e3: '0 24px 48px -16px rgba(30,37,66,.40), 0 2px 6px rgba(30,37,66,.10)',
  },
  texture: {
    /** Paper grain, barely there. An SVG noise tile. */
    grain:
      "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.85' numOctaves='3' stitchTiles='stitch'/%3E%3CfeColorMatrix values='0 0 0 0 .2 0 0 0 0 .16 0 0 0 0 .1 0 0 0 .55 0'/%3E%3C/filter%3E%3Crect width='160' height='160' filter='url(%23n)'/%3E%3C/svg%3E\")",
  },
  size: {
    /** The phone tab bar, before the safe area. */
    tabHeight: '64px',
    /** The desktop sidebar. */
    sidebar: '224px',
    /** The widest a page's content grows. */
    content: '1180px',
  },
} as const;

/**
 * Where the layout changes. CSS media queries cannot read custom properties,
 * so `ui/system.css` repeats these two numbers; this is their definition.
 *
 * - below `tablet`: phone. One column, bottom tab bar, sticky top bar.
 * - `tablet` up: wider gutters, actions sit beside page titles.
 * - `desktop` up: ink sidebar replaces the tab bar; two-column pages.
 */
export const breakpoints = {
  tablet: 600,
  desktop: 900,
} as const;
